"use client";

import { useMemo, type ReactNode } from "react";
import { cn } from "../../lib/utils";
import ArthaLinkSmIcon from "../icons/arthalinkSmIcon";
import EditBrushIcon from "../icons/editBrushIcon";
import type { LinkedPlatform, ShareOption } from "./share-bottom-sheet";
import { ShareLinkRow } from "./share-link-row";

export type ShareMode = "profile" | "job";

export interface ShareJobData {
  title: string;
  company: string;
  location?: string;
  salary?: string;
  logoUrl?: string;
}

export interface ShareOverviewProps {
  profileName: string;
  profileHandle: string;
  profileAvatar?: string;
  shareLink: string;
  themeColor: string;
  linkedPlatforms: LinkedPlatform[];
  shareOptions: ShareOption[];
  onCopyLink?: () => void;
  onEditSettings?: () => void;
  onLinkedPlatformSelect?: (platform: LinkedPlatform) => void;
  onShareOptionSelect?: (option: ShareOption) => void;
  /** Share mode - profile or job */
  mode?: ShareMode;
  /** Job data required when mode is "job" */
  jobData?: ShareJobData;
  /** Hide the edit settings button */
  hideEditButton?: boolean;
  /** Optional custom preview card shown instead of the default profile card */
  previewCard?: ReactNode;
}

export function ShareOverview({
  profileName,
  profileHandle,
  profileAvatar,
  shareLink,
  themeColor,
  linkedPlatforms,
  shareOptions,
  onCopyLink,
  onEditSettings,
  onLinkedPlatformSelect,
  onShareOptionSelect,
  mode = "profile",
  jobData,
  hideEditButton,
  previewCard,
}: ShareOverviewProps) {
  // Filter out share options that are already in linked platforms
  const filteredShareOptions = useMemo(() => {
    const linkedIds = new Set(linkedPlatforms.map((p) => p.id.toLowerCase()));
    return shareOptions.filter((option) => !linkedIds.has(option.id.toLowerCase()));
  }, [linkedPlatforms, shareOptions]);
  return (
    <div className="flex flex-col gap-4 pb-3">
      {previewCard ?? (
        <div
          className="relative rounded-lg border border-black px-4 pb-6 pt-6"
          style={{ background: `linear-gradient(90deg, ${themeColor} 0%, ${themeColor} 100%)` }}
        >
          {!hideEditButton && onEditSettings && (
            <button
              type="button"
              onClick={onEditSettings}
              className="absolute right-3 top-3 rounded-md border border-black bg-white p-1.5 text-[#344054] shadow-[0_2px_0_rgba(0,0,0,0.08)]"
              aria-label="Edit share settings"
            >
              <EditBrushIcon />
            </button>
          )}
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-[78px] w-[78px] items-center justify-center rounded-full">
              {profileAvatar ? (
                <img
                  src={profileAvatar}
                  alt={profileName}
                  className="h-[70px] w-[70px] rounded-full object-cover"
                />
              ) : (
                <span className="text-xl font-bold uppercase text-black">{profileName.charAt(0)}</span>
              )}
            </div>
            <div className="flex flex-col items-center gap-1">
              <p className="text-[20px] font-black uppercase leading-none text-black font-phudu">{profileName}</p>
              <div className="flex items-center gap-1 text-sm text-[#58616F]">
                <ArthaLinkSmIcon className="h-3.5 w-3.5" />
                <span>{profileHandle}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <ShareLinkRow link={shareLink} onCopy={onCopyLink} />

      {linkedPlatforms.length > 0 && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#344054]">Linked Platforms</p>
          <div className="flex gap-2">
            {linkedPlatforms.map((platform) => {
              // All platforms are now clickable
              return (
                <button
                  key={platform.id}
                  type="button"
                  className="flex w-[63px] flex-col items-center gap-2 text-center hover:opacity-80 transition-opacity"
                  onClick={() => {
                    onLinkedPlatformSelect?.(platform);
                    platform.onClick?.();
                  }}
                >
                  <div className="flex h-[42px] w-[42px] items-center justify-center rounded-md bg-black">
                    {platform.icon}
                  </div>
                  <p className="text-[10px] leading-tight text-[#344054]">{platform.label}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {filteredShareOptions.length > 0 && (
        <>
          <div className="h-px bg-[#D9D9D9]" />
          <div className="-mx-3 overflow-x-auto px-3">
            <div className="-mx-1 flex gap-2 px-1 pb-1 whitespace-nowrap">
              {filteredShareOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="flex min-w-[63px] flex-col items-center gap-1.5 text-center hover:opacity-80 transition-opacity"
                  onClick={() => {
                    if (onShareOptionSelect) {
                      onShareOptionSelect(option);
                    } else {
                      option.onClick?.();
                    }
                  }}
                >
                  <div
                    className={cn(
                      "flex h-[42px] w-[42px] items-center justify-center rounded-[5px] border border-black",
                      option.className
                    )}
                  >
                    {option.icon}
                  </div>
                  <p className="text-[10px] leading-tight text-[#344054]">{option.label}</p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
